import {
    Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { BlockOutlined, CheckCircleOutline, ContentCopyOutlined, EditOutlined, HomeWorkOutlined, LinkOffOutlined, LockOpenOutlined, Refresh, SensorDoorOutlined, SyncOutlined, VpnKeyOutlined } from "@mui/icons-material"
import { FC, useCallback, useEffect, useState } from "react"
import _ from 'lodash'
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { toastMsg } from "./smartLockToast"
import { TABLE_HEIGHT } from "../../configs"

interface AdminUser {
    _id: string
    name: string
    phone?: string
    email?: string
    profilePhotos?: any
}

type LockType = 'bnb' | 'rental'
// what the server says the signed-in user is: only 'super' may change, open or sync locks; 'admin' and 'user' can link them
type Role = 'super' | 'admin' | 'user'

// names only - the list never shows who lives in a unit
interface LockLink {
    propertyName?: string
    unitName?: string
    unitLabel?: string
}

interface PropertyOption {
    _id: string
    name: string
    town?: string
}

interface UnitOption {
    _id: string
    name?: string
    labels: { _id: string, unitLabel: string }[]
}

interface SmartLockRow {
    _id: string
    lockId: number
    lockName?: string
    lockAlias?: string
    lockMac?: string
    electricQuantity?: number
    syncedAt?: string
    type?: LockType
    admin?: AdminUser
    disabled?: boolean
    frozen?: boolean            // TTLock confirmed the lock is frozen
    enforced?: boolean          // the lock really can't be opened (frozen, or credentials expired by an earlier version)
    credentialCount?: number    // credentials recorded for restoring
    expiredCount?: number       // how many of those are expired right now
    hasGateway?: boolean
    propertyId?: string
    unitLabelId?: string
    link?: LockLink
}

type TypeFilter = 'all' | LockType | 'unclassified'

interface EditState {
    lock: SmartLockRow
    type: LockType | ''
    admin: AdminUser | null
    saving: boolean
    error: string | null
}

interface LinkState {
    lock: SmartLockRow
    property: PropertyOption | null
    units: UnitOption[]
    loadingUnits: boolean
    unitId: string
    unitLabelId: string
    saving: boolean
    error: string | null
}

type DurationUnit = 'minutes' | 'hours' | 'days'
const UNIT_MINUTES: Record<DurationUnit, number> = { minutes: 1, hours: 60, days: 1440 }
const MAX_PASSCODE_MINUTES = 365 * 24 * 60

const formatWhen = (ms: number) => new Date(ms).toLocaleString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

interface PasscodeState {
    lock: SmartLockRow
    amount: string
    unit: DurationUnit
    generating: boolean
    error: string | null
    result: { code: string, endDate: number } | null   // the passcode is only ever held here, in memory
    copied: boolean
}

interface UnlockState {
    lock: SmartLockRow
    unlocking: boolean
    error: string | null
}

interface StateProps {
    locks: SmartLockRow[]
    loading: boolean
    refreshing: boolean
    syncing: boolean
    typeFilter: TypeFilter
    edit: EditState | null
    togglingId: string | null
    unlinkingId: string | null
    checkingStateId: string | null
    passcode: PasscodeState | null
    unlock: UnlockState | null
    role: Role
    link: LinkState | null
}

const TYPE_LABELS: Record<LockType, string> = { bnb: 'BnB', rental: 'Rental' }

const adminLabel = (u: AdminUser) => u.name?.trim() || u.email || u.phone || ''

const lockTitle = (lock: SmartLockRow) => lock.lockAlias || lock.lockName || `Lock ${lock.lockId}`

// Searches Sheltergram users on the server as the admin types.
const AdminPicker: FC<{ value: AdminUser | null, disabled?: boolean, onChange: (user: AdminUser | null) => void }> = ({ value, disabled, onChange }) => {
    const [options, setOptions] = useState<AdminUser[]>([])
    const [loading, setLoading] = useState(false)

    // eslint-disable-next-line
    const search = useCallback(_.debounce((searchVal: string) => {
        setLoading(true)
        Apis({ module: 'Users', action: 'getUsers', searchVal, status: 'all', page: 0, pageSize: 10 })
            .then((resp: any) => setOptions(resp.users || []))
            .catch(() => setOptions([]))
            .finally(() => setLoading(false))
    }, 400), [])

    useEffect(() => () => search.cancel(), [search])

    return (
        <Autocomplete
            fullWidth
            disabled={disabled}
            value={value}
            options={options}
            loading={loading}
            filterOptions={x => x}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            getOptionLabel={adminLabel}
            onOpen={() => search('')}
            onInputChange={(_e, text, reason) => { if (reason === 'input') search(text) }}
            onChange={(_e, user) => onChange(user)}
            renderOption={(props, user) => (
                <li {...props} key={user._id}>
                    <Avatar src={user.profilePhotos?.small?.url} sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }} />
                    <Box>
                        <Typography sx={{ fontSize: 13 }}>{adminLabel(user)}</Typography>
                        {(user.phone || user.email) && (
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{user.phone || user.email}</Typography>
                        )}
                    </Box>
                </li>
            )}
            renderInput={params => <TextField {...params} label="Admin" placeholder="Search by name, phone or email" />}
        />
    )
}

const linkText = (lock: SmartLockRow) => {
    if (!lock.unitLabelId) return 'No unit label'
    return [lock.link?.unitName, lock.link?.unitLabel].filter(Boolean).join(' · ') || 'Unit label removed'
}

const propertyLabel = (p: PropertyOption) => p.town ? `${p.name} · ${p.town}` : p.name

// Searches the properties the signed-in user may link locks in (all of them for admins) as they type.
const PropertyPicker: FC<{ value: PropertyOption | null, disabled?: boolean, onChange: (property: PropertyOption | null) => void }> = ({ value, disabled, onChange }) => {
    const [options, setOptions] = useState<PropertyOption[]>([])
    const [loading, setLoading] = useState(false)

    // eslint-disable-next-line
    const search = useCallback(_.debounce((text: string) => {
        setLoading(true)
        Apis({ module: 'Locks', action: 'searchLinkProperties', search: text })
            .then((resp: any) => setOptions(resp.properties || []))
            .catch(() => setOptions([]))
            .finally(() => setLoading(false))
    }, 400), [])

    useEffect(() => () => search.cancel(), [search])

    return (
        <Autocomplete
            fullWidth
            disabled={disabled}
            value={value}
            options={options}
            loading={loading}
            filterOptions={x => x}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            getOptionLabel={propertyLabel}
            onOpen={() => search('')}
            onInputChange={(_e, text, reason) => { if (reason === 'input') search(text) }}
            onChange={(_e, property) => onChange(property)}
            renderInput={params => <TextField {...params} label="Property" placeholder="Search by property name" />}
        />
    )
}

const AdminSmartLocks: FC = () => {
    const [state, setState] = useState<StateProps>({
        locks: [],
        loading: true,
        refreshing: false,
        syncing: false,
        typeFilter: 'all',
        edit: null,
        togglingId: null,
        unlinkingId: null,
        checkingStateId: null,
        passcode: null,
        unlock: null,
        role: 'user',
        link: null
    })

    const { locks, loading, refreshing, syncing, typeFilter, edit, togglingId, unlinkingId, checkingStateId, passcode, unlock, role, link } = state
    const isSuper = role === 'super'

    const fetchLocks = ({ newFilter, processVariable }: any = {}) => {
        const Filter = newFilter || state.typeFilter
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, typeFilter: Filter }
        Apis({ module: 'Locks', action: 'getSmartLocks', type: Filter })
            .then((resp: any) => {
                if (resp.error) toastMsg('error', resp.message)
                setState(s => resp.error ? { ...s, ...afterLoad } : { ...s, ...afterLoad, locks: resp.locks || [], role: resp.role || s.role })
            })
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    const syncLocks = () => {
        setState(s => ({ ...s, syncing: true }))
        Apis({ module: 'Locks', action: 'syncSmartLocks' })
            .then((resp: any) => {
                setState(s => ({ ...s, syncing: false }))
                toastMsg(resp.error ? 'error' : 'success', resp.message)
                if (!resp.error) fetchLocks()
            })
            .catch(() => {
                setState(s => ({ ...s, syncing: false }))
                toastMsg('error', 'Could not sync locks. Please try again.')
            })
    }

    // Links the lock to a unit label (property -> unit -> label). The server checks the role: admins can use any property,
    // anyone else only the ones they own or manage.
    const openLink = (lock: SmartLockRow) =>
        setState(s => ({ ...s, link: { lock, property: null, units: [], loadingUnits: false, unitId: '', unitLabelId: '', saving: false, error: null } }))

    const closeLink = () => setState(s => (s.link?.saving ? s : { ...s, link: null }))

    const pickProperty = (property: PropertyOption | null) => {
        setState(s => (s.link ? { ...s, link: { ...s.link, property, units: [], unitId: '', unitLabelId: '', loadingUnits: Boolean(property), error: null } } : s))
        if (!property) return
        Apis({ module: 'Locks', action: 'getPropertyUnitLabels', propertyId: property._id })
            .then((resp: any) => setState(s => {
                if (!s.link || s.link.property?._id !== property._id) return s   // the answer to an earlier pick
                return resp.error
                    ? { ...s, link: { ...s.link, loadingUnits: false, error: resp.message } }
                    : { ...s, link: { ...s.link, loadingUnits: false, units: resp.units || [] } }
            }))
            .catch(() => setState(s => (s.link?.property?._id === property._id ? { ...s, link: { ...s.link, loadingUnits: false, error: 'Could not load the units. Please try again.' } } : s)))
    }

    const changeLink = (patch: Partial<Pick<LinkState, 'unitId' | 'unitLabelId'>>) =>
        setState(s => (s.link ? { ...s, link: { ...s.link, ...patch, error: null } } : s))

    const sendLink = (payload: any, done: string) => {
        if (!link) return
        const { lock } = link
        setState(s => (s.link ? { ...s, link: { ...s.link, saving: true, error: null } } : s))
        Apis({ module: 'Locks', ...payload, smartLockId: lock._id })
            .then((resp: any) => {
                if (resp.error)
                    return setState(s => (s.link ? { ...s, link: { ...s.link, saving: false, error: resp.message } } : s))
                setState(s => ({ ...s, link: null }))
                toastMsg('success', `${lockTitle(lock)}: ${done}`)
                fetchLocks()
            })
            .catch(() => setState(s => (s.link ? { ...s, link: { ...s.link, saving: false, error: 'Could not reach the server. Please try again.' } } : s)))
    }

    const saveLink = () => link?.property && sendLink(
        { action: 'linkSmartLock', propertyId: link.property._id, unitId: link.unitId, unitLabelId: link.unitLabelId },
        'linked to the unit label.'
    )

    const removeLink = () => sendLink({ action: 'unlinkSmartLock' }, 'unlinked.')

    // The same unlink, straight from the row - no need to open the dialog first.
    const unlinkDirect = (lock: SmartLockRow) => {
        setState(s => ({ ...s, unlinkingId: lock._id }))
        Apis({ module: 'Locks', action: 'unlinkSmartLock', smartLockId: lock._id })
            .then((resp: any) => {
                setState(s => ({ ...s, unlinkingId: null }))
                if (resp.error) return toastMsg('error', resp.message)
                toastMsg('success', `${lockTitle(lock)}: unlinked.`)
                fetchLocks()
            })
            .catch(() => {
                setState(s => ({ ...s, unlinkingId: null }))
                toastMsg('error', 'Could not reach the server. Please try again.')
            })
    }

    const openEdit = (lock: SmartLockRow) =>
        setState(s => ({ ...s, edit: { lock, type: lock.type || '', admin: lock.admin || null, saving: false, error: null } }))

    const closeEdit = () => setState(s => (s.edit?.saving ? s : { ...s, edit: null }))

    const changeEdit = (patch: Partial<Pick<EditState, 'type' | 'admin'>>) =>
        setState(s => (s.edit ? { ...s, edit: { ...s.edit, ...patch, error: null } } : s))

    const saveEdit = () => {
        if (!edit) return
        const { lock, type, admin } = edit
        setState(s => (s.edit ? { ...s, edit: { ...s.edit, saving: true, error: null } } : s))
        Apis({ module: 'Locks', action: 'updateSmartLock', smartLockId: lock._id, type, adminId: admin?._id || null })
            .then((resp: any) => {
                if (resp.error)
                    return setState(s => (s.edit ? { ...s, edit: { ...s.edit, saving: false, error: resp.message } } : s))
                setState(s => ({
                    ...s,
                    edit: null,
                    locks: s.locks.map(l => l._id !== lock._id ? l : { ...l, type: type || undefined, admin: admin || undefined })
                }))
                toastMsg('success', `${lockTitle(lock)} updated.`)
            })
            .catch(() => setState(s => (s.edit ? { ...s, edit: { ...s.edit, saving: false, error: 'Could not save. Please try again.' } } : s)))
    }

    // Whether the door is open or closed right now - live, through the gateway, only for the row that was asked for (never for
    // the whole list at once: most lock models have no sensor for this, and it is a slower live query).
    const checkOpenState = (lock: SmartLockRow) => {
        setState(s => ({ ...s, checkingStateId: lock._id }))
        Apis({ module: 'Locks', action: 'checkSmartLockOpenState', smartLockId: lock._id })
            .then((resp: any) => {
                if (resp.error) return toastMsg('error', resp.message)
                const label = resp.state === 'open' ? 'Open' : resp.state === 'closed' ? 'Closed' : 'Unknown (this lock may not have a status sensor)'
                toastMsg(resp.state === 'unknown' ? 'error' : 'success', `${lockTitle(lock)}: ${label}`)
            })
            .catch(() => toastMsg('error', 'Could not reach the server. Please try again.'))
            .finally(() => setState(s => (s.checkingStateId === lock._id ? { ...s, checkingStateId: null } : s)))
    }

    // Unlocks the door now through the gateway; asks first because it physically opens the lock.
    const openUnlock = (lock: SmartLockRow) => setState(s => ({ ...s, unlock: { lock, unlocking: false, error: null } }))

    const closeUnlock = () => setState(s => (s.unlock?.unlocking ? s : { ...s, unlock: null }))

    const confirmUnlock = () => {
        if (!unlock) return
        const { lock } = unlock
        setState(s => (s.unlock ? { ...s, unlock: { ...s.unlock, unlocking: true, error: null } } : s))
        Apis({ module: 'Locks', action: 'unlockSmartLock', smartLockId: lock._id })
            .then((resp: any) => {
                if (!resp.error) toastMsg('success', `${lockTitle(lock)}: ${resp.message}`)
                setState(s => resp.error
                    ? (s.unlock ? { ...s, unlock: { ...s.unlock, unlocking: false, error: resp.message } } : s)
                    : { ...s, unlock: null })
            })
            .catch(() => setState(s => (s.unlock ? { ...s, unlock: { ...s.unlock, unlocking: false, error: 'Could not reach the server. Please try again.' } } : s)))
    }

    const openPasscode = (lock: SmartLockRow) =>
        setState(s => ({ ...s, passcode: { lock, amount: '1', unit: 'hours', generating: false, error: null, result: null, copied: false } }))

    const closePasscode = () => setState(s => (s.passcode?.generating ? s : { ...s, passcode: null }))

    const changePasscode = (patch: Partial<Pick<PasscodeState, 'amount' | 'unit'>>) =>
        setState(s => (s.passcode ? { ...s, passcode: { ...s.passcode, ...patch, error: null } } : s))

    const generatePasscode = () => {
        if (!passcode) return
        const minutes = Number(passcode.amount) * UNIT_MINUTES[passcode.unit]
        setState(s => (s.passcode ? { ...s, passcode: { ...s.passcode, generating: true, error: null } } : s))
        Apis({ module: 'Locks', action: 'generateSmartLockPasscode', smartLockId: passcode.lock._id, durationMinutes: minutes })
            .then((resp: any) => setState(s => {
                if (!s.passcode) return s
                return resp.error
                    ? { ...s, passcode: { ...s.passcode, generating: false, error: resp.message } }
                    : { ...s, passcode: { ...s.passcode, generating: false, result: { code: resp.passcode, endDate: resp.endDate } } }
            }))
            .catch(() => setState(s => (s.passcode ? { ...s, passcode: { ...s.passcode, generating: false, error: 'Could not reach the server. Please try again.' } } : s)))
    }

    const copyPasscode = () => {
        if (!passcode?.result) return
        Promise.resolve(navigator.clipboard?.writeText(passcode.result.code))
            .then(() => setState(s => (s.passcode ? { ...s, passcode: { ...s.passcode, copied: true } } : s)))
            .catch(() => undefined)
    }

    // Disable freezes the lock through TTLock and Enable unfreezes it (Enable also restores any credentials an earlier version
    // of Disable expired). It takes a few seconds through the gateway. The row only changes once TTLock confirms; after a
    // failure the list is reloaded to show the real state.
    const toggleDisabled = (lock: SmartLockRow, disabled: boolean) => {
        setState(s => ({ ...s, togglingId: lock._id }))
        Apis({ module: 'Locks', action: 'setSmartLockDisabled', smartLockId: lock._id, disabled })
            .then((resp: any) => {
                if (resp.error) {
                    toastMsg('error', resp.message)
                    setState(s => ({ ...s, togglingId: null }))
                    return fetchLocks()
                }
                toastMsg('success', resp.message)
                setState(s => ({
                    ...s,
                    togglingId: null,
                    locks: s.locks.map(l => l._id === lock._id
                        ? { ...l, disabled, frozen: disabled, enforced: disabled, credentialCount: disabled ? l.credentialCount : 0, expiredCount: disabled ? l.expiredCount : 0 }
                        : l)
                }))
                if (disabled) fetchLocks()
            })
            .catch(() => {
                toastMsg('error', 'Could not reach the server. Reloading to show the real state.')
                setState(s => ({ ...s, togglingId: null }))
                fetchLocks()
            })
    }

    useEffect(() => {
        fetchLocks({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    const passcodeMinutes = passcode ? Number(passcode.amount) * UNIT_MINUTES[passcode.unit] : 0
    const passcodeValid = Boolean(passcode) && /^\d+$/.test(passcode?.amount || '') && passcodeMinutes >= 1 && passcodeMinutes <= MAX_PASSCODE_MINUTES

    const unchanged = Boolean(edit) && (edit?.type || '') === (edit?.lock.type || '') && (edit?.admin?._id || null) === (edit?.lock.admin?._id || null)

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Smart Locks" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {locks.length > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {locks.length.toLocaleString()} lock{locks.length === 1 ? '' : 's'}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={typeFilter}
                            onChange={(e: SelectChangeEvent) => fetchLocks({ newFilter: e.target.value as TypeFilter, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All Types</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="bnb">BnB</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="rental">Rental</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="unclassified">Unclassified</MenuItem>
                        </Select>
                    </FormControl>
                    {isSuper && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={syncLocks}
                            disabled={syncing}
                            startIcon={syncing ? <CircularProgress size={16} /> : <SyncOutlined />}
                            sx={{ borderRadius: 2, textTransform: 'none', fontSize: 13 }}
                        >
                            Sync from TTLock
                        </Button>
                    )}
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchLocks({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!locks.length || loading ?
                <NoData message={typeFilter !== 'all' ? 'No locks match this filter.' : isSuper ? 'No smart locks yet. Use "Sync from TTLock" to import them.' : 'No smart locks found.'} loading={loading} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Lock</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Battery</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Unit label</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Admin</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Last Synced</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locks.map(lock => (
                                    <TableRow hover key={lock._id}>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{lockTitle(lock)}</Typography>
                                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                                ID {lock.lockId}{lock.lockAlias && lock.lockName ? ` · ${lock.lockName}` : ''}{lock.hasGateway === false ? ' · No gateway' : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: (lock.electricQuantity ?? 100) < 20 ? 'error.main' : 'inherit' }}>
                                            {lock.electricQuantity == null ? '—' : `${lock.electricQuantity}%`}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {lock.type
                                                ? <Chip size="small" variant="outlined" color={lock.type === 'bnb' ? 'info' : 'success'} label={TYPE_LABELS[lock.type]} />
                                                : <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Unclassified</Typography>}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {lock.propertyId
                                                ? <>
                                                    <Typography sx={{ fontSize: 13 }}>{linkText(lock)}</Typography>
                                                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{lock.link?.propertyName || 'Property removed'}</Typography>
                                                </>
                                                : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {lock.admin
                                                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar src={lock.admin.profilePhotos?.small?.url} sx={{ width: 24, height: 24, fontSize: 12 }} />
                                                    {adminLabel(lock.admin)}
                                                </Box>
                                                : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {lock.disabled
                                                ? lock.enforced
                                                    ? <Chip size="small" variant="outlined" color="error" label="Disabled" />
                                                    : <Tooltip title={lock.credentialCount
                                                        ? 'Not frozen. Some passcodes, fingerprints or cards are still expired from an earlier disable. Press Enable to restore them, or Disable to finish disabling the lock.'
                                                        : 'Only marked disabled in Sheltergram; the lock was never frozen, so it still opens. Press Disable to freeze it.'}>
                                                        <Chip size="small" variant="outlined" color="warning" label="Not enforced" />
                                                    </Tooltip>
                                                : <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Enabled</Typography>}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {lock.syncedAt
                                                ? new Date(lock.syncedAt).toLocaleString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={lock.propertyId ? 'Change the unit label this lock belongs to' : 'Link this lock to a unit label'}>
                                                <IconButton size="small" onClick={() => openLink(lock)}>
                                                    <HomeWorkOutlined sx={{ fontSize: 19 }} />
                                                </IconButton>
                                            </Tooltip>
                                            {lock.propertyId && (
                                                <Tooltip title="Unlink from the unit label">
                                                    <span>
                                                        <IconButton size="small" onClick={() => unlinkDirect(lock)} disabled={unlinkingId === lock._id}>
                                                            {unlinkingId === lock._id
                                                                ? <CircularProgress size={18} />
                                                                : <LinkOffOutlined sx={{ fontSize: 19 }} />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {isSuper && <>
                                            <Tooltip title="Check whether the door is open or closed right now (only locks with that sensor can answer)">
                                                <span>
                                                    <IconButton size="small" onClick={() => checkOpenState(lock)} disabled={checkingStateId === lock._id}>
                                                        {checkingStateId === lock._id ? <CircularProgress size={17} /> : <SensorDoorOutlined sx={{ fontSize: 19 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Unlock now through the gateway (it locks itself again automatically)">
                                                <IconButton size="small" onClick={() => openUnlock(lock)}>
                                                    <LockOpenOutlined sx={{ fontSize: 19 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Generate a passcode that expires after a time you choose">
                                                <IconButton size="small" onClick={() => openPasscode(lock)}>
                                                    <VpnKeyOutlined sx={{ fontSize: 19 }} />
                                                </IconButton>
                                            </Tooltip>
                                            {(!lock.disabled || !lock.enforced) && (
                                                <Tooltip title={lock.disabled
                                                    ? 'Disable lock (freezes it, or expires its credentials if it cannot be frozen)'
                                                    : 'Disable lock (freezes it through TTLock: no passcode, fingerprint, card or app can open it until you press Enable. If the lock model cannot be frozen, its passcodes, fingerprints and cards are expired instead. Nothing is deleted)'}>
                                                    <span>
                                                        <IconButton size="small" onClick={() => toggleDisabled(lock, true)} disabled={togglingId === lock._id}>
                                                            {togglingId === lock._id
                                                                ? <CircularProgress size={18} />
                                                                : <BlockOutlined sx={{ fontSize: 19, color: 'error.main' }} />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {lock.disabled && (
                                                <Tooltip title="Enable lock (unfreezes it, or restores the passcodes, fingerprints and cards that were expired, so they work again)">
                                                    <span>
                                                        <IconButton size="small" onClick={() => toggleDisabled(lock, false)} disabled={togglingId === lock._id}>
                                                            <CheckCircleOutline sx={{ fontSize: 19, color: 'success.main' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(lock)}>
                                                    <EditOutlined sx={{ fontSize: 19 }} />
                                                </IconButton>
                                            </Tooltip>
                                            </>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            }

            <Dialog open={Boolean(unlock)} onClose={closeUnlock} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>
                    {unlock ? `Unlock ${lockTitle(unlock.lock)}?` : 'Unlock'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14, mt: 1 }}>
                        This unlocks the door right now. It locks itself again automatically after a few seconds.
                    </Typography>
                    {unlock?.lock.frozen ? (
                        <Typography sx={{ fontSize: 13, color: 'warning.main', mt: 1.5 }}>
                            This lock is frozen, so TTLock may refuse to unlock it until you press Enable.
                        </Typography>
                    ) : unlock?.lock.disabled && (
                        <Typography sx={{ fontSize: 13, color: 'warning.main', mt: 1.5 }}>
                            This lock is disabled but not frozen, so this will still unlock it.
                        </Typography>
                    )}
                    {unlock?.error && (
                        <Typography sx={{ fontSize: 13, color: 'error.main', mt: 1.5 }}>{unlock.error}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeUnlock} disabled={unlock?.unlocking} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={confirmUnlock}
                        disabled={unlock?.unlocking}
                        startIcon={unlock?.unlocking ? <CircularProgress size={16} color="inherit" /> : undefined}
                        sx={{ textTransform: 'none' }}
                    >
                        Unlock
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(passcode)} onClose={closePasscode} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>
                    Generate passcode
                    {passcode && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 400 }}>
                            {lockTitle(passcode.lock)} · ID {passcode.lock.lockId}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {passcode?.lock.frozen ? (
                        <Typography sx={{ fontSize: 13, color: 'warning.main', mt: 1 }}>
                            This lock is frozen, so the passcode won't open it until you press Enable.
                        </Typography>
                    ) : passcode?.lock.disabled && (
                        <Typography sx={{ fontSize: 13, color: 'warning.main', mt: 1 }}>
                            This lock is disabled but not frozen, so a passcode generated now will open it until it expires.
                        </Typography>
                    )}
                    {passcode?.result ? (
                        <Box sx={{ pt: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>PASSCODE</Typography>
                            <Typography sx={{ fontSize: 40, fontWeight: 700, letterSpacing: 6, fontFamily: 'monospace', my: 1 }}>
                                {passcode.result.code}
                            </Typography>
                            <Typography sx={{ fontSize: 13 }}>Works until {formatWhen(passcode.result.endDate)}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>
                                Sheltergram doesn't keep this code, so copy it now. It is also listed in the TTLock app.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField
                                    label="Expires after"
                                    type="number"
                                    value={passcode?.amount || ''}
                                    disabled={passcode?.generating}
                                    onChange={e => changePasscode({ amount: e.target.value })}
                                    inputProps={{ min: 1, step: 1 }}
                                    error={Boolean(passcode?.amount) && !passcodeValid}
                                    sx={{ flex: 1 }}
                                    autoFocus
                                />
                                <FormControl sx={{ minWidth: 120 }} disabled={passcode?.generating}>
                                    <InputLabel id="passcode-unit-label">Unit</InputLabel>
                                    <Select
                                        labelId="passcode-unit-label"
                                        label="Unit"
                                        value={passcode?.unit || 'hours'}
                                        onChange={(e: SelectChangeEvent) => changePasscode({ unit: e.target.value as DurationUnit })}
                                    >
                                        <MenuItem value="minutes">Minutes</MenuItem>
                                        <MenuItem value="hours">Hours</MenuItem>
                                        <MenuItem value="days">Days</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Typography sx={{ fontSize: 13, color: passcode?.amount && !passcodeValid ? 'error.main' : 'text.secondary' }}>
                                {passcodeValid
                                    ? `Works from now until ${formatWhen(Date.now() + passcodeMinutes * 60000)}.`
                                    : 'Enter a whole number: 1 minute up to 365 days.'}
                            </Typography>
                            {passcode?.error && (
                                <Typography sx={{ fontSize: 13, color: 'error.main' }}>{passcode.error}</Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {passcode?.result ? (
                        <>
                            <Button onClick={copyPasscode} startIcon={<ContentCopyOutlined />} sx={{ textTransform: 'none' }}>
                                {passcode.copied ? 'Copied' : 'Copy'}
                            </Button>
                            <Button variant="contained" onClick={closePasscode} sx={{ textTransform: 'none' }}>Done</Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={closePasscode} disabled={passcode?.generating} sx={{ textTransform: 'none' }}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={generatePasscode}
                                disabled={passcode?.generating || !passcodeValid}
                                startIcon={passcode?.generating ? <CircularProgress size={16} color="inherit" /> : undefined}
                                sx={{ textTransform: 'none' }}
                            >
                                Generate
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(link)} onClose={closeLink} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>
                    Link to a unit label
                    {link && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 400 }}>
                            {lockTitle(link.lock)} · ID {link.lock.lockId}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                        <PropertyPicker value={link?.property || null} disabled={link?.saving} onChange={pickProperty} />
                        <FormControl fullWidth disabled={link?.saving || link?.loadingUnits || !link?.units.length}>
                            <InputLabel id="link-unit-label">Unit</InputLabel>
                            <Select
                                labelId="link-unit-label"
                                label="Unit"
                                value={link?.unitId || ''}
                                onChange={(e: SelectChangeEvent) => changeLink({ unitId: e.target.value, unitLabelId: '' })}
                            >
                                {link?.units.map(unit => <MenuItem key={unit._id} value={unit._id}>{unit.name || 'Unnamed unit'}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth disabled={link?.saving || !link?.unitId}>
                            <InputLabel id="link-unitlabel-label">Unit label</InputLabel>
                            <Select
                                labelId="link-unitlabel-label"
                                label="Unit label"
                                value={link?.unitLabelId || ''}
                                onChange={(e: SelectChangeEvent) => changeLink({ unitLabelId: e.target.value })}
                            >
                                {link?.units.find(unit => unit._id === link.unitId)?.labels.map(label => <MenuItem key={label._id} value={label._id}>{label.unitLabel}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {link?.loadingUnits && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading units...</Typography>}
                        {link && link.property && !link.loadingUnits && !link.units.length && !link.error && (
                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>This property has no units yet.</Typography>
                        )}
                        {link?.error && (
                            <Typography sx={{ fontSize: 13, color: 'error.main' }}>{link.error}</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {link?.lock.propertyId && (
                        <Button onClick={removeLink} disabled={link.saving} color="error" sx={{ textTransform: 'none', mr: 'auto' }}>
                            Unlink
                        </Button>
                    )}
                    <Button onClick={closeLink} disabled={link?.saving} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveLink}
                        disabled={link?.saving || !link?.unitLabelId}
                        startIcon={link?.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                        sx={{ textTransform: 'none' }}
                    >
                        Link
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(edit)} onClose={closeEdit} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>
                    Edit lock
                    {edit && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 400 }}>
                            {lockTitle(edit.lock)} · ID {edit.lock.lockId}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                        <FormControl fullWidth disabled={edit?.saving}>
                            <InputLabel id="lock-type-label" shrink>Type</InputLabel>
                            <Select
                                labelId="lock-type-label"
                                label="Type"
                                notched
                                displayEmpty
                                value={edit?.type || ''}
                                onChange={(e: SelectChangeEvent) => changeEdit({ type: e.target.value as LockType | '' })}
                            >
                                <MenuItem value=""><Typography sx={{ color: 'text.secondary' }}>Unclassified</Typography></MenuItem>
                                <MenuItem value="bnb">BnB</MenuItem>
                                <MenuItem value="rental">Rental</MenuItem>
                            </Select>
                        </FormControl>
                        <AdminPicker
                            value={edit?.admin || null}
                            disabled={edit?.saving}
                            onChange={admin => changeEdit({ admin })}
                        />
                        {edit?.error && (
                            <Typography sx={{ fontSize: 13, color: 'error.main' }}>{edit.error}</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeEdit} disabled={edit?.saving} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveEdit}
                        disabled={edit?.saving || unchanged}
                        startIcon={edit?.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                        sx={{ textTransform: 'none' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default AdminSmartLocks
